// Pure, side-effect-free helpers + constants extracted from Designer.tsx so the
// page component shrinks and the logic is unit-testable on its own.

export type Tool = 'product' | 'text' | 'design'

export const CANVAS_W = 640
export const CANVAS_H = 720

// Fallback used only when a garment has no printArea persisted (legacy / new
// product without admin setup). Real values come from product.printArea.
// 40 x 50 cm centred on the 640x720 canvas at the studio's 1.25 mm/canvas-px
// ratio (320 x 400 canvas-px = 400 x 500 mm).
export const PRINT_DEFAULT = { left: 160, top: 160, width: 320, height: 400 }

// SVG silhouette fallback used until per-product mockup photos are uploaded.
export function silhouettePath(slug: string): string {
  switch (slug) {
    case 'hoodie':
      return 'M 130 60 Q 200 -20 270 60 L 280 80 L 250 78 Q 230 90 200 90 Q 170 90 150 78 L 120 80 Z ' +
             'M 80 90 L 130 60 L 200 90 L 270 60 L 320 90 L 360 160 L 330 185 L 300 168 L 300 462 L 100 462 L 100 168 L 70 185 L 40 160 Z'
    case 'long-sleeve':
      return 'M 150 50 L 120 30 Q 160 22 200 22 Q 240 22 280 30 L 250 50 Q 230 78 200 78 Q 170 78 150 50 Z ' +
             'M 60 80 L 150 50 L 200 78 L 250 50 L 340 80 L 380 280 L 350 295 L 320 200 L 300 200 L 300 462 L 100 462 L 100 200 L 80 200 L 50 295 L 20 280 Z'
    case 'polo':
      return 'M 150 50 L 120 30 Q 160 22 200 22 Q 240 22 280 30 L 250 50 Q 230 70 200 75 Q 170 70 150 50 Z ' +
             'M 80 80 L 150 50 L 175 100 L 200 92 L 225 100 L 250 50 L 320 80 L 360 150 L 330 175 L 300 158 L 300 462 L 100 462 L 100 158 L 70 175 L 40 150 Z'
    case 'hat':
      // Six-panel cap: domed crown + sweatband stripe + flat brim extending right.
      return 'M 110 200 Q 200 100 290 200 L 290 240 L 110 240 Z ' +    // crown
             'M 110 245 L 290 245 L 290 260 L 110 260 Z ' +              // sweatband
             'M 70 265 L 360 265 L 360 290 L 70 290 Q 70 280 70 265 Z'   // brim (extends right)
    default:
      return 'M 150 50 L 120 30 Q 160 22 200 22 Q 240 22 280 30 L 250 50 Q 230 78 200 78 Q 170 78 150 50 Z ' +
             'M 80 80 L 150 50 L 200 78 L 250 50 L 320 80 L 360 150 L 330 175 L 300 158 L 300 462 L 100 462 L 100 158 L 70 175 L 40 150 Z'
  }
}

// Per-location print-zone width heuristic in mm — used to convert canvas px
// to real-world dimensions for the live size + DPI readout. Print locations
// don't yet carry a mm scale, so we infer from the location name. Front /
// back default to 280 mm (A4-ish tee print); sleeves and hems to ~80 mm.
export function locationPrintWidthMm(name: string): number {
  const n = name.toLowerCase()
  if (n.includes('sleeve') || n.includes('cuff')) return 80
  if (n.includes('hem')) return 80
  if ((n.includes('chest') || n.includes('pocket')) && (n.includes('left') || n.includes('right'))) return 90
  return 280
}

export interface GarmentTotals { totalQty: number; total: number }

// Sum the selected garment sizes into a quantity + dollar total. Pure; mirrors
// the inline useMemo it replaced. Behaviour preserved exactly (dollar math, the
// same as before — not the server's cents-first rounding; see note in DTF #21).
export function garmentTotals(
  product: { variants: Array<{ size: string | null; price: number }> } | null | undefined,
  qty: Record<string, number>,
): GarmentTotals {
  if (!product) return { totalQty: 0, total: 0 }
  let totalQty = 0, total = 0
  for (const [size, q] of Object.entries(qty)) {
    if (!q) continue
    const v = product.variants.find((x) => x.size === size)
    if (!v) continue
    totalQty += q
    total += q * v.price
  }
  return { totalQty, total }
}

// Detects whether a saved Fabric JSON snapshot contains real user objects
// (not just the print-area guide rect + label). Used to filter empty
// locations out of the submitted spec and to drive sidebar thumbnails.
export function canvasHasMedia(json: { objects?: Array<{ type?: string; text?: string; strokeDashArray?: unknown }> } | null | undefined): boolean {
  if (!json) return false
  const objs = json.objects ?? []
  return objs.some((o) =>
    !(o.type === 'rect' && Array.isArray(o.strokeDashArray)) &&
    !(o.type === 'text' && o.text === 'PRINT AREA'),
  )
}

// A rectangle in canvas-px space. Print zones, object bounding rects and the
// design's overall bounding box all share this shape.
export interface Box { left: number; top: number; width: number; height: number }

// Union bounding box of a set of rectangles. Returns null for an empty set.
// Mirrors the inline reduction Designer.userBoundingBox() used to do over the
// canvas objects' getBoundingRect() results.
export function unionBox(rects: Box[]): Box | null {
  if (rects.length === 0) return null
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const b of rects) {
    minX = Math.min(minX, b.left)
    maxX = Math.max(maxX, b.left + b.width)
    minY = Math.min(minY, b.top)
    maxY = Math.max(maxY, b.top + b.height)
  }
  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY }
}

// Translation that recentres the design bbox inside the print zone.
export function centerDelta(zone: Box, bbox: Box): { dx: number; dy: number } {
  return {
    dx: (zone.left + zone.width / 2) - (bbox.left + bbox.width / 2),
    dy: (zone.top + zone.height / 2) - (bbox.top + bbox.height / 2),
  }
}

// ── Text transform helpers ────────────────────────────────────────────────────

// Returns true when the text is non-empty, all-uppercase, and not all lowercase
// (guards mixed-case single chars like "A" counting as uppercase even though
// toLowerCase() would change nothing).
export function isUppercaseText(text: string): boolean {
  return text.trim().length > 0 && text !== text.toLowerCase() && text === text.toUpperCase()
}

// Toggles between FULL UPPERCASE and Title Case-per-word, preserving spaces and
// newlines. Splitting on /([ \n])/ keeps the delimiters in the token array so
// join('') re-assembles the original whitespace exactly.
export function toggleUppercaseText(text: string): string {
  if (!text.trim()) return text
  if (isUppercaseText(text)) {
    return text.split(/([ \n])/).map((tok) =>
      tok === ' ' || tok === '\n' ? tok : tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase()
    ).join('')
  }
  return text.toUpperCase()
}

export type FitMode = 'width' | 'height' | 'contain'

// Scale factor + centred target origin for fitting the design bbox to the print
// zone. 'width'/'height' fit that single axis; 'contain' fits both (min scale).
// The per-object transform stays in the component; this is just the geometry.
export function fitPlacement(
  mode: FitMode,
  zone: Box,
  bbox: Box,
): { factor: number; targetLeft: number; targetTop: number } {
  const sx = zone.width / bbox.width
  const sy = zone.height / bbox.height
  const factor = mode === 'width' ? sx : mode === 'height' ? sy : Math.min(sx, sy)
  const newW = bbox.width * factor
  const newH = bbox.height * factor
  return {
    factor,
    targetLeft: zone.left + (zone.width - newW) / 2,
    targetTop: zone.top + (zone.height - newH) / 2,
  }
}
